/**
 * CondoDocumentsBuyerView — the buyer reviews delivered packages and
 * acknowledges receipt of everything, or reports missing documents.
 *
 * The acknowledgement is evidence of receipt only: the review period already
 * started when the documents were delivered to the buyer's account (legal
 * §5.3), and acknowledging on a later day never moves it.
 *
 * `readOnly` (an admin, or a viewer who is neither party) shows the same
 * documents without the acknowledgement form.
 */

import React, { useState } from 'react';
import styles from './CondoDocumentsPanel.module.css';
import {
  BUYER_RECEIPT_ALL_TEXT,
  BUYER_RECEIPT_ATTESTATION_VERSION,
  BUYER_RECEIPT_PARTIAL_TEXT,
  CONDO_DOCUMENTS_COPY as COPY,
} from './condoDocumentsCopy';
import {
  checklistForPackage,
  cureDate,
  hasReportedMissing,
  isDelivered,
  isLateMissingRequest,
  itemLabel,
  labelForCode,
  lateRequestFor,
} from './condoDocumentsModel';
import { formatLongLocalDate, formatLongTimestamp } from './condoDocumentsFormat';
import {
  PACKAGE_STATUS_DELIVERED,
  PACKAGE_STATUS_RECEIVED,
} from '../../agents/condoDocumentsAgent';
import CondoDocumentList from './CondoDocumentList';

const MODE_ALL = 'all';
/** WarningOut code for a missing-documents report made after the (3c) period. */
const LATE_REQUEST_WARNING = 'condo_missing_request_late';
const MODE_MISSING = 'missing';

function ReceiptForm({ pkg, checklist, summary, busy, onAcknowledge }) {
  const [mode, setMode] = useState(MODE_ALL);
  const [missing, setMissing] = useState([]);
  const [signatureName, setSignatureName] = useState('');
  const [certify, setCertify] = useState(false);
  const [formError, setFormError] = useState(null);
  const disabled = Boolean(busy);
  const attestation = summary?.attestations?.buyer_receipt || {};
  const attestationVersion = attestation.version || BUYER_RECEIPT_ATTESTATION_VERSION;
  const attestationText = mode === MODE_ALL
    ? attestation.text || BUYER_RECEIPT_ALL_TEXT
    : attestation.text_missing || BUYER_RECEIPT_PARTIAL_TEXT;
  const requestByDate = formatLongLocalDate(pkg.request_by_local_date);
  const radioName = `condo-receipt-mode-${pkg.id}`;
  const canSubmit = certify && signatureName.trim().length > 0 && !disabled;

  const toggleMissing = (code) => {
    setMissing((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    if (mode === MODE_MISSING && missing.length === 0) {
      setFormError(COPY.missingPickerRequired);
      return;
    }
    setFormError(null);
    await onAcknowledge(pkg.id, {
      allReceived: mode === MODE_ALL,
      missingItems: mode === MODE_MISSING ? missing : [],
      signatureName: signatureName.trim(),
      certify,
      attestationVersion,
    });
  };

  return (
    <form className={styles.attestation} onSubmit={handleSubmit} data-testid="condo-receipt-form">
      <fieldset className={styles.fieldset}>
        <legend className={styles.subheading}>{COPY.receiptHeading}</legend>
        <label className={styles.radioRow}>
          <input
            type="radio"
            name={radioName}
            value={MODE_ALL}
            checked={mode === MODE_ALL}
            onChange={() => { setMode(MODE_ALL); setFormError(null); }}
            disabled={disabled}
          />
          <span>{COPY.receiptAll}</span>
        </label>
        <label className={styles.radioRow}>
          <input
            type="radio"
            name={radioName}
            value={MODE_MISSING}
            checked={mode === MODE_MISSING}
            onChange={() => setMode(MODE_MISSING)}
            disabled={disabled}
          />
          <span>{COPY.receiptMissing}</span>
        </label>
      </fieldset>

      {mode === MODE_MISSING && (
        <p className={styles.hint} data-testid="condo-missing-help">
          {requestByDate ? COPY.buyerMissingHelp({ requestByDate }) : COPY.buyerMissingHelpNoDate}
        </p>
      )}

      {mode === MODE_MISSING && (
        <fieldset className={`${styles.fieldset} ${styles.missingPicker}`}>
          <legend className={styles.fieldLabel}>{COPY.missingPickerLegend}</legend>
          {checklist.map((item) => (
            <label key={item.code} className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={missing.includes(item.code)}
                onChange={() => toggleMissing(item.code)}
                disabled={disabled}
              />
              <span>{itemLabel(item)}</span>
            </label>
          ))}
        </fieldset>
      )}

      <blockquote className={styles.attestationQuote}>{attestationText}</blockquote>
      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={certify}
          onChange={(e) => setCertify(e.target.checked)}
          disabled={disabled}
        />
        <span>{COPY.attestationCheckbox}</span>
      </label>
      <label className={styles.fieldLabel} htmlFor={`condo-receipt-sign-${pkg.id}`}>
        {COPY.signatureLabel}
      </label>
      <input
        id={`condo-receipt-sign-${pkg.id}`}
        type="text"
        className={styles.input}
        value={signatureName}
        autoComplete="name"
        onChange={(e) => setSignatureName(e.target.value)}
        disabled={disabled}
      />
      {formError && <p className={styles.fieldError} role="alert">{formError}</p>}
      <button type="submit" className={styles.buttonPrimary} disabled={!canSubmit}>
        {busy === 'acknowledge'
          ? COPY.submittingReceipt
          : mode === MODE_ALL ? COPY.confirmReceiptButton : COPY.reportMissingButton}
      </button>
    </form>
  );
}

function ReceivedStatus({ pkg, items, summary }) {
  if (hasReportedMissing(pkg)) {
    // After the request period: forwarded, but the review period is unchanged
    // and there is no cure deadline (WI.condo_docs.buyer.late_request, L4).
    const lateRequest = lateRequestFor(summary, pkg);
    const late = isLateMissingRequest(pkg) || Boolean(lateRequest);
    const requestDate = lateRequest?.requested_local_date
      ? formatLongLocalDate(lateRequest.requested_local_date)
      : formatLongTimestamp(lateRequest?.requested_at || pkg.missing_request_at || pkg.buyer_received_at);
    const requestByDate = formatLongLocalDate(lateRequest?.request_by_local_date || pkg.request_by_local_date);
    // The late-request warning banner already explains it: say it once.
    const warnedLate = (summary?.warnings || []).some((w) => w.code === LATE_REQUEST_WARNING);
    const cure = cureDate(summary, pkg);
    const cureText = cure.localDate ? formatLongLocalDate(cure.localDate) : formatLongTimestamp(cure.at);
    return (
      <div
        className={`${styles.notice} ${styles.noticeWarning}`}
        data-testid="condo-buyer-missing-status"
        data-late={late ? 'true' : undefined}
      >
        <p>
          {late && !warnedLate && requestDate && requestByDate
            ? COPY.buyerLateRequest({ requestDate, requestByDate })
            : COPY.buyerYouReportedMissing({ date: requestDate })}
        </p>
        <ul className={styles.plainList}>
          {pkg.buyer_receipt.missing_items.map((code) => <li key={code}>{labelForCode(code, items)}</li>)}
        </ul>
        {!late && cureText && <p>{COPY.buyerCureDeadline({ date: cureText })}</p>}
      </div>
    );
  }
  return (
    <p className={styles.statusLine} data-testid="condo-buyer-confirmed">
      {COPY.buyerConfirmedReceipt({ date: formatLongTimestamp(pkg.buyer_received_at) })}
    </p>
  );
}

export default function CondoDocumentsBuyerView({
  summary,
  packages,
  transactionId,
  busy,
  actions,
  readOnly = false,
}) {
  const delivered = packages.filter(isDelivered);
  const items = summary?.items || [];

  const intro = readOnly
    ? null
    : <p className={styles.intro} data-testid="condo-buyer-intro">{COPY.buyerIntro}</p>;

  if (delivered.length === 0) {
    return (
      <>
        {intro}
        <p className={styles.muted} data-testid="condo-buyer-waiting">{COPY.buyerWaiting}</p>
      </>
    );
  }

  return (
    <div className={styles.view} data-testid="condo-buyer-view">
      {intro}
      {delivered.map((pkg) => {
        const checklist = checklistForPackage(pkg, summary, packages);
        const kindLabel = COPY.packageKind[pkg.kind] || COPY.packageKind.initial;
        return (
          <article key={pkg.id} className={styles.packageCard} data-testid={`condo-package-${pkg.id}`}>
            <header className={styles.packageHeader}>
              <h4 className={styles.subheading}>
                {COPY.packageHeading({ kind: kindLabel, sequence: pkg.sequence ?? 1 })}
              </h4>
              <span className={styles.muted}>
                {COPY.delivered({ deliveredDate: formatLongTimestamp(pkg.delivered_at) })}
              </span>
            </header>
            <CondoDocumentList pkg={pkg} checklist={checklist} transactionId={transactionId} />
            {pkg.status === PACKAGE_STATUS_RECEIVED && <ReceivedStatus pkg={pkg} items={items} summary={summary} />}
            {pkg.status === PACKAGE_STATUS_DELIVERED && !readOnly && (
              <ReceiptForm
                pkg={pkg}
                checklist={checklist}
                summary={summary}
                busy={busy}
                onAcknowledge={actions.acknowledge}
              />
            )}
          </article>
        );
      })}
    </div>
  );
}
